import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Overline, Disclaimer } from "../components/shared";
import { Markdown } from "../components/Markdown";
import { Switch } from "../components/ui/switch";
import { cn } from "../lib/utils";
import { Bot, Send, Loader2, User, Sparkles, Globe, Database, Link2, ExternalLink } from "lucide-react";

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [useWeb, setUseWeb] = useState(true);
  const [webEnabled, setWebEnabled] = useState(true);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/suggestions").then((r) => setSuggestions(r.data.suggestions));
    api.get("/assistant/status").then((r) => setWebEnabled(r.data.web_search_enabled)).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId, use_web: useWeb && webEnabled });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, llm_ok: data.llm_ok, web_used: data.web_used, web_sources: data.web_sources || [], web_warning: data.web_warning }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, something went wrong reaching the analyst.", llm_ok: false }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="fade-up flex flex-col h-[calc(100vh-7rem)]" data-testid="assistant-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center"><Bot className="w-5 h-5 text-primary-foreground" /></div>
        <div><Overline>Embedded Analyst</Overline><h1 className="font-heading font-extrabold text-2xl tracking-tight leading-tight">AI Research Assistant</h1></div>
      </div>

      <div className="flex-1 bg-card border border-border/60 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {messages.length === 0 && (
            <div className="max-w-2xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-accent rounded-sm flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-primary" /></div>
                <div className="text-sm text-foreground leading-relaxed">
                  I'm your research analyst. Ask me <strong>anything</strong> — I blend the app's tracked-project database, {webEnabled ? "live web search," : "web search (needs setup),"} and general AI knowledge, and I label where each fact comes from.
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex items-center gap-1 text-[11px] border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm px-1.5 py-0.5"><Database className="w-3 h-3" /> App DB</span>
                    <span className="inline-flex items-center gap-1 text-[11px] border border-blue-200 bg-blue-50 text-blue-700 rounded-sm px-1.5 py-0.5"><Globe className="w-3 h-3" /> Web</span>
                    <span className="inline-flex items-center gap-1 text-[11px] border border-violet-200 bg-violet-50 text-violet-700 rounded-sm px-1.5 py-0.5"><Sparkles className="w-3 h-3" /> AI knowledge</span>
                  </div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="assistant-suggestion"
                    className="text-left text-sm border border-border/60 rounded-sm px-3 py-2.5 hover:border-primary/40 hover:bg-muted/50 transition-colors inline-flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />{s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={cn("flex items-start gap-3", m.role === "user" && "flex-row-reverse")}>
              <div className={cn("w-8 h-8 rounded-sm flex items-center justify-center shrink-0", m.role === "user" ? "bg-primary" : "bg-accent")}>
                {m.role === "user" ? <User className="w-4 h-4 text-primary-foreground" /> : <Bot className="w-4 h-4 text-primary" />}
              </div>
              <div className="max-w-3xl min-w-0">
                <div className={cn("text-sm leading-relaxed rounded-sm px-3.5 py-2.5", m.role === "user" ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-muted/60 text-foreground border border-border/60")}>
                  {m.role === "user" ? m.content : <Markdown>{m.content}</Markdown>}
                </div>
                {m.role !== "user" && m.web_used && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 mt-1"><Globe className="w-3 h-3" /> Included live web results</span>
                )}
                {m.role !== "user" && !m.web_used && m.web_warning && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 mt-1"><Globe className="w-3 h-3" /> {m.web_warning}</span>
                )}
                {m.role !== "user" && (m.web_sources?.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assistant-web-sources">
                    {m.web_sources.map((s, j) => (
                      <a key={j} href={s.url} target="_blank" rel="noreferrer" title={s.title}
                         className="inline-flex items-center gap-1 max-w-[240px] text-[11px] border border-border bg-card rounded-sm px-1.5 py-0.5 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors">
                        <Link2 className="w-3 h-3 shrink-0" /><span className="truncate">{s.title || s.url}</span><ExternalLink className="w-2.5 h-2.5 shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-accent flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-primary" /></div>
              <div className="bg-muted/60 border border-border/60 rounded-sm px-3.5 py-2.5 text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {useWeb && webEnabled ? "Researching the database and the web…" : "Analyzing the database…"}</div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border/60 p-3">
          <div className="flex items-center justify-between mb-2">
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <Switch checked={useWeb && webEnabled} onCheckedChange={setUseWeb} disabled={!webEnabled} data-testid="assistant-web-toggle" />
              <Globe className="w-3.5 h-3.5" /> Search the web {webEnabled ? "" : "· add a Tavily key to enable"}
            </label>
            <span className="text-[11px] text-muted-foreground">Model configurable in Settings</span>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything — projects, tokenomics, news, concepts…" data-testid="assistant-input"
              className="flex-1 h-11 px-3.5 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring" />
            <button type="submit" disabled={loading || !input.trim()} data-testid="assistant-send"
              className="h-11 px-4 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50 transition-colors inline-flex items-center gap-2 font-semibold text-sm">
              <Send className="w-4 h-4" /> Send
            </button>
          </form>
          <Disclaimer className="mt-2 border-0 bg-transparent px-0 py-0">Answers blend the app database, live web, and AI knowledge — each fact labelled with its source. Research only, not investment advice.</Disclaimer>
        </div>
      </div>
    </div>
  );
}
